import React, { useState, useEffect, createContext } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import ParentSidebar from './ParentSidebar';
import { deriveNameFromEmail } from '../../utils/nameFromEmail';

const layoutCss = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
  .pl-root { display:flex; height:100vh; background:#f4f6fb; font-family:'Sora',sans-serif; overflow:hidden; }
  .pl-sidebar { flex-shrink:0; height:100vh; overflow:hidden; }
  .pl-body { flex:1; display:flex; flex-direction:column; min-width:0; height:100vh; overflow:hidden; }
  .pl-topbar { display:flex; align-items:center; justify-content:space-between; padding:14px 24px; background:white; border-bottom:1px solid #e8ebf4; position:sticky; top:0; z-index:10; gap:12px; }
  .pl-breadcrumb { font-size:.76rem; color:#9094a8; }
  .pl-breadcrumb span { color:#1a1d2e; font-weight:600; }
  .pl-topbar-right { display:flex; align-items:center; gap:12px; }
  .pl-child-switcher { display:flex; align-items:center; gap:8px; background:#f0f2f7; border-radius:10px; padding:4px 6px; overflow-x:auto; scrollbar-width:none; flex-wrap:wrap; }
  .pl-child-switcher::-webkit-scrollbar { display:none; }
  .pl-child-btn { display:flex; align-items:center; gap:6px; padding:6px 12px; border-radius:8px; border:none; font-family:'Sora',sans-serif; font-size:.74rem; font-weight:600; cursor:pointer; white-space:nowrap; flex-shrink:0; transition:all .15s; background:transparent; color:#6b7194; }
  .pl-child-btn.active { background:white; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.1); }
  .pl-child-avatar { width:22px; height:22px; border-radius:6px; display:flex; align-items:center; justify-content:center; font-size:.62rem; font-weight:700; flex-shrink:0; }
  .pl-user { display:flex; align-items:center; gap:8px; cursor:pointer; position:relative; }
  .pl-user-avatar { width:34px; height:34px; border-radius:10px; background:linear-gradient(135deg,#f97316,#ef4444); color:white; display:flex; align-items:center; justify-content:center; font-weight:700; font-size:.8rem; }
  .pl-user-name { font-size:.82rem; font-weight:600; color:#0f1123; }
  .pl-user-role { font-size:.68rem; color:#9094a8; }
  .pl-logout { padding:7px 14px; border:1px solid #fecaca; border-radius:9px; background:#fff1f1; color:#ef4444; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; transition:background .15s; }
  .pl-logout:hover { background:#fee2e2; }
  .pl-mob-right { display:flex; align-items:center; gap:8px; }
  .pl-main { flex:1; padding:24px; overflow-y:auto; overflow-x:hidden; }
  .pl-mob-overlay { display:none; }
  .pl-mob-bar { display:none; }
  @media(max-width:768px) {
    .pl-sidebar { display:none; position:fixed; left:0; top:0; height:100vh; z-index:100; }
    .pl-sidebar.open { display:flex; }
    .pl-mob-overlay { display:block; position:fixed; inset:0; background:rgba(0,0,0,.4); z-index:99; }
    .pl-mob-bar { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; background:white; border-bottom:1px solid #e8ebf4; position:sticky; top:0; z-index:10; }
    .pl-mob-menu { width:32px; height:32px; border:none; border-radius:8px; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
    .pl-mob-menu svg { width:16px; height:16px; stroke:#1a1d2e; stroke-width:2; }
    .pl-logout { padding:6px 10px; font-size:.72rem; }
    .pl-topbar { display:none; }
    .pl-main { padding:16px; }
    .pl-child-switcher { max-width:200px; }
    .pl-mob-child-row { display:block !important; }
  }
`;

const CHILD_COLORS = ['#f97316','#3b6ef8','#10c98e','#8b5cf6','#ef4444','#06b6d4'];

export const ChildContext = createContext({ activeChild: null, children: [], setActiveChild: ()=>{} });

export default function ParentLayout() {
  const { user, logout } = useAuth();
  const displayName = user?.name || deriveNameFromEmail(user?.email) || 'Parent';
  const navigate         = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [children,    setChildren]    = useState([]);
  const [activeChild, setActiveChild] = useState(null);

  useEffect(() => {
    // Add cache-busting timestamp to avoid 304 cached response with stale children
    api.get('/parent/children?_=' + Date.now())
      .then(r => {
        const kids = r.data || [];
        setChildren(kids);
        if (kids.length && !activeChild) setActiveChild(kids[0]);
      })
      .catch(() => {});
  }, []);

  const initials = (n='') => n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'P';

  return (
    <ChildContext.Provider value={{ activeChild, children, setActiveChild }}>
      <style>{layoutCss}</style>
      <div className="pl-root">

        {sidebarOpen && <div className="pl-mob-overlay" onClick={()=>setSidebarOpen(false)}/>}
        <div className={`pl-sidebar${sidebarOpen?' open':''}`}>
          <ParentSidebar onClose={()=>setSidebarOpen(false)} activeChild={activeChild} />
        </div>

        <div className="pl-body">
          {/* Mobile top bar */}
          <div className="pl-mob-bar">
            <button className="pl-mob-menu" onClick={()=>setSidebarOpen(true)}>
              <svg viewBox="0 0 24 24" fill="none"><path d="M3 12h18M3 6h18M3 18h18" strokeLinecap="round"/></svg>
            </button>
            <div style={{fontSize:'.82rem',fontWeight:700,color:'#0f1123'}}>Parent Portal</div>
            <div className="pl-mob-right">
              <div className="pl-user-avatar" style={{width:30,height:30,fontSize:'.72rem'}}>{initials(displayName)}</div>
              <button className="pl-logout" onClick={()=>{ logout(); navigate('/login'); }}>Logout</button>
            </div>
          </div>

          {/* Desktop top bar */}
          <div className="pl-topbar">
            <div className="pl-breadcrumb">School Portal / <span>Parent</span></div>

            {/* Child tabs — show for 1 or more children */}
            {children.length > 0 && (
              <div className="pl-child-switcher">
                {children.map((c, i) => (
                  <button key={c._id}
                    className={`pl-child-btn${activeChild?._id===c._id?' active':''}`}
                    onClick={()=>setActiveChild(c)}>
                    <div className="pl-child-avatar"
                      style={{background:`${CHILD_COLORS[i%CHILD_COLORS.length]}20`,color:CHILD_COLORS[i%CHILD_COLORS.length]}}>
                      {(c.name||'?')[0].toUpperCase()}
                    </div>
                    {c.name}
                  </button>
                ))}
              </div>
            )}

            <div className="pl-topbar-right">
              <div className="pl-user">
                <div className="pl-user-avatar">{initials(displayName)}</div>
                <div>
                  <div className="pl-user-name">{displayName}</div>
                  <div className="pl-user-role">Parent</div>
                </div>
              </div>
              <button className="pl-logout" onClick={()=>{ logout(); navigate('/login'); }}>Logout</button>
            </div>
          </div>

          {/* Mobile child switcher — hidden on desktop, shown on mobile via CSS */}
          {children.length > 0 && (
            <div className="pl-mob-child-row" style={{padding:'8px 16px',background:'white',borderBottom:'1px solid #e8ebf4',display:'none'}}>
              <div className="pl-child-switcher" style={{maxWidth:'100%'}}>
                {children.map((c,i) => (
                  <button key={c._id}
                    className={`pl-child-btn${activeChild?._id===c._id?' active':''}`}
                    onClick={()=>setActiveChild(c)}>
                    <div className="pl-child-avatar"
                      style={{background:`${CHILD_COLORS[i%CHILD_COLORS.length]}20`,color:CHILD_COLORS[i%CHILD_COLORS.length]}}>
                      {(c.name||'?')[0].toUpperCase()}
                    </div>
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <main className="pl-main">
            <Outlet />
          </main>
        </div>
      </div>
    </ChildContext.Provider>
  );
}